import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, UseControllerProps, useWatch } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import { SHOW_INACTIVE_PATIENTS_SEARCH } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import { useDebounce } from "hooks/";
import { useQueryPatientsQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";

type SelectPatientProps = {
  label?: string;
  required?: boolean;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  sx?: SxProps;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  showInactive?: boolean;
  query?: string;
  multiple?: boolean;
};

export type RhfSelectPatient<T extends FieldValues> = SelectPatientProps &
  UseControllerProps<T>;

export default function SelectPatient<T extends FieldValues>({
  control,
  name,
  label = "By Patient",
  required,
  onOptionSelected,
  openOnFocus = false,
  autoFocus = false,
  showInactive,
  query,
  multiple,
  sx
}: RhfSelectPatient<T>) {
  const flags = useFlags();
  const showInactivePatients =
    showInactive || flags[SHOW_INACTIVE_PATIENTS_SEARCH];
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const [search, setSearch] = useState<string>("");

  const patientOption = useWatch({ control, name }) as {
    label: string;
    value: string;
  };

  // Word-level filter: all typed words must appear in option label.
  // This handles patients with middle names (e.g., "John Smith" matches "John Michael Smith").
  const filterOptions = (
    options: { label?: string; value: string }[],
    { inputValue }: { inputValue: string }
  ) => {
    const words = inputValue.toLowerCase().trim().split(/\s+/);
    return options.filter((option) => {
      const label = (option.label || "").toLowerCase();
      return words.every((word) => label.includes(word));
    });
  };

  useEffect(() => {
    setSearch(patientOption?.label ?? "");
  }, [patientOption]);

  const { value: dSearch } = useDebounce(search);
  const { data: patients = [], isLoading } = useQueryPatientsQuery(
    {
      search: dSearch,
      baseFilter: {
        preferred_clinic: currentPractice?.limit_to_current_clinic
          ? currentClinic?.id
          : undefined
      },
      query: query ?? "{id,full_name,is_active}",
      is_active: showInactivePatients ? undefined : true
    },
    {
      skip:
        !currentClinic?.id ||
        !currentPractice?.id ||
        !dSearch ||
        dSearch.length < 3
    }
  );

  const patientStatus: { [patientId: number]: boolean } = useMemo(() => {
    return patients.reduce((acc: { [patientId: number]: boolean }, patient) => {
      acc[patient.id] = Boolean(patient.is_active);
      return acc;
    }, {});
  }, [patients]);

  return (
    <AutocompleteFormField
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      name={name}
      label={label}
      isLoading={isLoading}
      required={required}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      filterOptions={filterOptions}
      noOptionsText={
        !search?.length ? "Search patient by name..." : "No options"
      }
      options={patients.map((patient: Patient) => ({
        label: patient.full_name,
        value: String(patient.id),
        is_active: patient.is_active
      }))}
      getOptionLabel={(option) => {
        return option?.label ?? "";
      }}
      renderOption={(props: object, option: any) => (
        <ListItem {...props}>
          <ListItemText
            sx={{
              textDecoration:
                showInactivePatients && !patientStatus[option.value]
                  ? "line-through"
                  : undefined
            }}
          >
            {option?.label}
            {option?.is_active === false && " (Inactive)"}
          </ListItemText>
          {showInactivePatients && !patientStatus[option.value] && (
            <Chip label="Deactivated" sx={{ ml: 1 }} />
          )}
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onTextInputChange={(text) => {
        setSearch(text);
      }}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          setSearch(option?.value ?? "");
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 250, ...sx }}
    />
  );
}
